/**
 * Shared handler for "advance without a correct answer" — covers two paths:
 *
 *   - TIMEOUT: the riddle's time limit has expired. Fired automatically by
 *     the timer component in location-view. No penalty.
 *   - SKIP: the player pressed the "Пропустить этап" button. Requires the
 *     game to have allowManualSkip enabled. Adds a penalty equal to the
 *     location's time limit to the player's totalTimeMs.
 *
 * Two safeguards prevent double-fire:
 *   1. `inFlight` module-level flag blocks concurrent calls (e.g. location-view timers both hitting zero in the same tick).
 *   2. `handledLocationIds` remembers which location IDs have already been
 *      advanced during this session, so a rapid re-entry (e.g. navigating
 *      back into location-view from chat after the timer already fired) does
 *      nothing.
 *
 * On success we mirror the server's updates into local state using the same
 * pattern as the correct-answer path in location-view: store nextKeys and
 * the next location's encryptedRiddle/encryptedHint, show a 2-second overlay,
 * then advance currentLocationIndex and navigate to 'riddle' (or 'results').
 *
 * Plan B Task 9 (2026-05-11) — full-screen overlay is now managed by
 * `bodyOverlays.register('auto-advance', cleanup, lc)`. The view that owns
 * the current activeLc is responsible for tying overlay teardown to its
 * own lifecycle: the kebab → leave race that used to leak a 92%-opaque
 * blanket overlay onto /join is closed by the lc.onDestroy hook registered
 * inside the body-overlays primitive. The old `clearAutoAdvanceOverlay`
 * export + module-level `let activeOverlay / activeOverlayTimer /
 * activeNavigateTimer` slots are gone — replace-semantics + lc.cleanup
 * together cover every case the slots used to cover.
 */

import { getState, setState } from '../store';
import { advanceLocationIndex } from '../store-actions';
import { navigate } from '../router';
import { advanceLocation } from '../api/game-api';
import { deriveKey } from '../crypto/crypto-utils';
import { anchorFromServer } from './time-utils';
import { showErrorToast } from '../components/team-event-toast';
import { register as registerBodyOverlay } from './body-overlays';
import type { ViewLifecycle } from './view-lifecycle';

let inFlight = false;
const handledLocationIds = new Set<string>();

/** Map a reason to the overlay title shown briefly before navigation. */
function overlayTitle(reason: 'TIMEOUT' | 'SKIP'): string {
  return reason === 'TIMEOUT' ? 'ВРЕМЯ ВЫШЛО' : 'ЭТАП ПРОПУЩЕН';
}

/** Format a millisecond duration as "+MM:SS" for toast messages. */
function formatPenalty(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `+${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Show a full-screen overlay on top of the current view. Reuses the same
 * look as the correct-answer overlay in location-view. The body-overlays
 * primitive provides replace-semantics: a second `register('auto-advance',
 * …, lc)` call evicts the previous overlay's cleanup before the new one
 * becomes active. View-destroy releases via `lc.onDestroy(cleanup)`,
 * which runs as part of `activeLc.cleanup()` — no separate sweep needed.
 *
 * `navigateTimerId` is registered alongside the DOM node + auto-dismiss
 * timer so a manual navigate during the 2s window (kebab → leave) cancels
 * the pending advance — without this the user would be yanked back to
 * /riddle 2s after they thought they had left. Same /review fix as the
 * pre-extraction `activeNavigateTimer` slot tracked.
 */
function showOverlay(
  title: string,
  subtitle: string,
  accent: 'success' | 'warning' | 'danger',
  lc: ViewLifecycle,
): { setNavigateTimer: (id: ReturnType<typeof setTimeout>) => void } {
  const color = accent === 'success' ? 'var(--success)'
              : accent === 'warning' ? 'var(--amber)'
              : 'var(--danger)';

  const el = document.createElement('div');
  el.id = 'auto-advance-overlay';
  el.style.cssText = `
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(10, 10, 15, 0.92);
    display: flex; justify-content: center; align-items: center;
    flex-direction: column; gap: var(--sp-lg);
    animation: fadeIn 0.2s ease-out;
  `;
  el.innerHTML = `
    <div class="text-display-large" style="color: ${color}; text-align: center;">${title}</div>
    ${subtitle ? `<div class="text-body-large" style="color: var(--text-secondary); text-align: center; padding: 0 var(--sp-lg);">${subtitle}</div>` : ''}
  `;
  document.body.appendChild(el);

  // Plain `setTimeout`, not `window.setTimeout` — the auto-advance unit test
  // (`tests/auto-advance.test.ts`) runs in a hand-rolled fake-document env
  // with no `window` stub, so `window.setTimeout` would throw and get
  // swallowed by triggerAdvance's catch, blocking the navigate path.
  let dismissTimerId: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    dismissTimerId = null;
    if (el.parentNode) el.parentNode.removeChild(el);
  }, 2000);

  // The deferred navigate timer set up by triggerAdvance / handleOfflineTimeout
  // is captured into this slot via the returned setter. Cleanup cancels both
  // timers and removes the DOM node. Same shape as the pre-extraction
  // `activeOverlayTimer + activeNavigateTimer` pair, now scoped to one call.
  let navigateTimerId: ReturnType<typeof setTimeout> | null = null;

  const cleanup = (): void => {
    if (dismissTimerId !== null) {
      clearTimeout(dismissTimerId);
      dismissTimerId = null;
    }
    if (navigateTimerId !== null) {
      clearTimeout(navigateTimerId);
      navigateTimerId = null;
    }
    if (el.parentNode) el.parentNode.removeChild(el);
  };

  registerBodyOverlay('auto-advance', cleanup, lc);

  return {
    setNavigateTimer(id: ReturnType<typeof setTimeout>): void {
      navigateTimerId = id;
    },
  };
}

/**
 * Trigger advancement. Idempotent: safe to call multiple times from multiple
 * views — the second call becomes a no-op.
 *
 * `lc` ties the overlay's lifetime to the calling view: when the view's
 * `activeLc.cleanup()` runs (route swap, kebab → leave, visibility-driven
 * remount), the overlay tears down with it. Callers are location-view's 4
 * timer callbacks, chat-view's watchdog interval, and game-menu's SKIP
 * action — all of which already hold an `activeLc` in scope.
 */
export async function triggerAdvance(reason: 'TIMEOUT' | 'SKIP', lc: ViewLifecycle): Promise<void> {
  if (inFlight) return;
  const state = getState();
  const loc = state.locations[state.currentLocationIndex];
  if (!loc) return;
  if (handledLocationIds.has(loc.id)) return;

  // /investigate 2026-05-04 (game 773697 demo run) — demo mode no-ops TIMEOUT.
  // The location-view timer applies `demoSpeedMultiplier` (50×) to elapsed
  // seconds, so the local timer hits 0 ~50× faster than the server's wall-
  // clock anchor. Server validates `anchor + limitMs > now` against real
  // time and rejects with «Время этапа ещё не истекло». Pre-fix this 403/409
  // surfaced as «Сессия устарела» (the 403 case classified as permanent auth
  // failure; backend now returns 409 but it's still a wasted round trip).
  // Demo bot is the legitimate advance path — it submits the correct answer
  // before the visual timer matters. SKIP stays open: a manual user click on
  // «пропустить» during a demo is a real user action, not a 50×-timer race.
  if (reason === 'TIMEOUT' && (state.demoSpeedMultiplier ?? 1) > 1) return;

  // Full-offline mode: no backend round-trip available. Derive the next key
  // locally using the answerHash chain (same as submitAnswer's correct path
  // in location-view). Only TIMEOUT supports offline — SKIP requires server
  // verification for the penalty to stick.
  if (state.fullOffline) {
    if (reason === 'SKIP') {
      console.warn('[auto-advance] SKIP not supported in full-offline mode');
      showErrorToast('Пропуск этапа недоступен в полностью офлайн-режиме');
      return;
    }
    await handleOfflineTimeout(loc, lc);
    return;
  }

  inFlight = true;
  try {
    const res = await advanceLocation(state.gameCode, {
      playerId: state.playerId,
      locationId: loc.id,
      reason,
    });
    if (!res.correct) return;

    handledLocationIds.add(loc.id);

    // Mirror server penalty into local state for UI display.
    if (typeof res.penaltyMs === 'number') {
      setState({ penaltyMs: res.penaltyMs });
    }

    // Store next riddle key + encrypted riddle/hint (same pattern as the
    // correct-answer path in location-view).
    if (res.nextRiddleKey) {
      const keys = [...state.nextKeys];
      keys[state.currentLocationIndex] = res.nextRiddleKey;
      setState({ nextKeys: keys });
    }
    if (res.nextEncryptedRiddle) {
      const nextIdx = state.currentLocationIndex + 1;
      if (nextIdx < state.locations.length) {
        const updatedLocations = [...state.locations];
        updatedLocations[nextIdx] = {
          ...updatedLocations[nextIdx],
          encryptedRiddle: res.nextEncryptedRiddle,
          encryptedHint: res.nextEncryptedHint || '',
          encryptedHints: res.nextEncryptedHints || updatedLocations[nextIdx].encryptedHints,
        };
        setState({ locations: updatedLocations });
      }
    }

    const isLast = state.currentLocationIndex >= state.locations.length - 1;
    // Session 53 post-ship — capture the TARGET index at fire time, NOT as
    // `getState().currentLocationIndex + 1` inside the deferred setTimeout.
    // In demo mode (50x multiplier) the backend's own TeamEvent ADVANCE WS
    // broadcast routinely beats this 2-second overlay and re-enters via
    // `location-view.ts::onTeamEvent` → `goTo(target)` which ALREADY bumps
    // `state.currentLocationIndex` to target. Adding 1 to the post-WS index
    // then over-advances (N → N+1 via WS, then N+1 → N+2 here) while
    // leaving `nextKeys[N+1]` unpopulated. On F5 the next mount reads
    // `nextKeys[currentLocationIndex - 1] = nextKeys[N+1]` = undefined and
    // navigates to `/results`. Pinning the target at the pre-advance snapshot
    // makes the deferred setState idempotent with whatever the WS handler did.
    const targetIndex = state.currentLocationIndex + 1;
    const penaltyAdded = reason === 'SKIP' ? loc.timeLimitSec * 1000 : 0;
    const subtitle = penaltyAdded > 0
      ? `Штраф ${formatPenalty(penaltyAdded)}`
      : 'Переход к следующей загадке';

    const overlay = showOverlay(overlayTitle(reason), subtitle, reason === 'TIMEOUT' ? 'warning' : 'danger', lc);

    // Wait 2 s while the overlay is visible, then navigate. Matches the
    // correct-answer UX in location-view. The navigate timer handle is
    // captured into the overlay's cleanup closure so a manual navigate
    // (kebab → leave during the 2s window) cancels it via lc.cleanup() →
    // bodyOverlays eviction.
    const navigateTimer = setTimeout(() => {
      if (isLast) {
        const now = Date.now();
        const current = getState();
        // totalTimeMs displayed on results view must equal (wall elapsed) +
        // (server-recorded penalty). Matches the backend leaderboard formula
        // so the player sees consistent numbers across both screens.
        const wall = now - current.gameStartedAt;
        const total = wall + current.penaltyMs;
        advanceLocationIndex(current.locations.length, {
          status: 'FINISHED',
          completedAt: now,
          totalTimeMs: total,
        });
        navigate('results');
      } else {
        // Only advance if we're still BEHIND the target — if the WS ADVANCE
        // event arrived during the overlay and already moved us to target
        // (or past), leave state alone and just re-render the current view.
        const current = getState();
        if (current.currentLocationIndex < targetIndex) {
          // /autoplan 2026-04-24 — seed timer from server anchor so the
          // submitter's timer doesn't drift from teammates who already
          // re-anchored via TeamEvent.ADVANCE. Helper falls back to Date.now()
          // when response lacks the fields (pre-fix backend / stale build).
          advanceLocationIndex(targetIndex, {
            locationStartedAt: anchorFromServer(res.locationEnteredAt, res.serverNow),
          });
        }
        navigate('riddle');
      }
    }, 2000);
    overlay.setNavigateTimer(navigateTimer);
  } catch (err) {
    console.warn('[auto-advance] request failed', err);
    // Surface the backend's localized message to the user. request<T> in
    // game-api.ts throws `new Error(err.error)` where err.error is the
    // server's GameStateException text («Время этапа ещё не истекло»,
    // «Только капитан может пропустить этап», «Ручной пропуск отключён
    // организатором», «Игрок без команды»). Pre-fix this was console-only,
    // leaving the captain who clicked Skip / the player whose timer fired
    // with no idea why nothing happened. Backlog item 3 / 2026-05-08 audit.
    const msg = err instanceof Error && err.message
      ? err.message
      : 'Не удалось перейти к следующему этапу';
    showErrorToast(msg);
  } finally {
    inFlight = false;
  }
}

/** Derive next key from local answerHash chain (offline timeout path). */
async function handleOfflineTimeout(
  loc: { id: string; answerHash?: string; timeLimitSec: number },
  lc: ViewLifecycle,
): Promise<void> {
  const state = getState();
  if (!loc.answerHash) {
    console.warn('[auto-advance] offline timeout: no answerHash on location');
    showErrorToast('Не удалось перейти автоматически — обратитесь к организатору');
    return;
  }
  handledLocationIds.add(loc.id);

  // Derive key from answerHash — matches backend GameService.deriveKey.
  const nextKeyBytes = await deriveKey(loc.answerHash);
  const nextKeyB64 = btoa(String.fromCharCode(...nextKeyBytes));
  const keys = [...state.nextKeys];
  keys[state.currentLocationIndex] = nextKeyB64;
  setState({ nextKeys: keys });

  const overlay = showOverlay(overlayTitle('TIMEOUT'), 'Переход к следующей загадке', 'warning', lc);

  // Session 53 post-ship — same target-index discipline as the online
  // path. Offline mode rarely has a WS racing ahead (no server), but
  // keeping both paths symmetric avoids diverging invariants.
  const isLast = state.currentLocationIndex >= state.locations.length - 1;
  const targetIndex = state.currentLocationIndex + 1;
  const navigateTimer = setTimeout(() => {
    if (isLast) {
      const now = Date.now();
      advanceLocationIndex(getState().locations.length, {
        status: 'FINISHED',
        completedAt: now,
        totalTimeMs: now - getState().gameStartedAt,
      });
      navigate('results');
    } else {
      const current = getState();
      if (current.currentLocationIndex < targetIndex) {
        // /autoplan 2026-04-24 — offline-only path. No server round-trip
        // means no server anchor available; Date.now() is authoritative by
        // design. Pinned by `timer-anchor-source-contract.test.ts` as the
        // single legitimate direct Date.now() write to locationStartedAt.
        advanceLocationIndex(targetIndex, {
          locationStartedAt: Date.now(),
        });
      }
      navigate('riddle');
    }
  }, 2000);
  overlay.setNavigateTimer(navigateTimer);
}

/**
 * Reset the handled-ids cache. Called when a player fully resets (leaves the
 * game, or a new game starts). Visible for testing.
 */
export function resetAutoAdvanceHandled(): void {
  handledLocationIds.clear();
  inFlight = false;
}

/** Allow auto-advance to re-fire for a specific location (e.g. after hint-navigate). */
export function clearAutoAdvanceForLocation(locationId: string): void {
  handledLocationIds.delete(locationId);
}
